import { useEffect, useRef, useState } from "react";
import { NLMailboxProfileType, NLNewsletterCategory, OnboardingProfile } from "@models/Models";
import ParticlesBackground from "@components/common/ParticlesBackground";
import { useOnboardingProfile } from "@providers/OnboardingContext";
import { Link, useNavigate } from "react-router-dom";
import { MxResult } from "@models/Enums";
import { ReactComponent as GmailIcon } from "@images/gmail_icon.svg";
import { ReactComponent as OutlookIcon } from "@images/outlook_logo.svg";
import { ReactComponent as MecoIcon } from "@images/meco_app_logo.svg";
import { ReactComponent as FilledArrow } from "@images/filled_arrow_icon.svg";
import { AddMailboxCard } from "@components/settings/connected-mailboxes/NewMailboxModal";
import { useAuth } from "@providers/AuthContext";
import Alert, { AlertState } from "@components/common/Alert";
import LoadingView, { LoadingState } from "@components/common/LoadingView";
import { deleteMailboxProfile } from "@utils/managers/networking/NetworkManager";

const Integration = (props) => {
    const navigate = useNavigate();
    const { onboardingProfile, setOnboardingProfile } = useOnboardingProfile();
    const { authUser } = useAuth();
    const [alertState, setAlertState] = useState<AlertState>({
		isShow: null,
		title: null,
		message: null,
		actionButton: null,
		dismissButton: null,
		actionButtonAction: undefined,
	});
    const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: false,
	});
    const isDisplayAlert = useRef<Boolean>(true);

	const addMailboxCards: AddMailboxCard[] = [
		{
			title: "Connect a Gmail",
			trialBadge: true,
			description: "Add your existing newsletters from your Gmail to Meco in seconds. Just sign in to your Gmail and select the newsletters you want to read in Meco going forward.",
			icon: <GmailIcon className="p-1" />,
			color: "#c71610",
			onClick: () => gmailOnClick(),
		},
		{
			title: "Connect an Outlook",
			trialBadge: true,
			description: "Add your existing newsletters from your Outlook to Meco in seconds. Just sign in to your Outlook and select the newsletters you want to read in Meco going forward.",
			icon: <OutlookIcon className="p-1 mr-[2px]" />,
			color: "#0364B8",
			onClick: () => outlookOnClick(),
		},
		{
			title: "Create a Meco Inbox",
			trialBadge: false,
			description: "Get a new Meco email address just for newsletters. Subscribe to any newsletter with your personal Meco address to read it in the app.",
			icon: <MecoIcon className="p-1" />,
			color: "#191D2E",
			onClick: () => mecoInboxOnClick(),
		},
	];

	const gmailOnClick = () => {
		if (onboardingProfile?.persistence.mailboxProfile && isDisplayAlert.current) {
			showReplaceMailboxAlert(gmailOnClick);
			return;
		}
		navigate("/get-started/connect-gmail");
	};

	const outlookOnClick = () => {
		if (onboardingProfile?.persistence.mailboxProfile && isDisplayAlert.current) {
			showReplaceMailboxAlert(outlookOnClick);
			return;
		}
		navigate("/get-started/connect-outlook");
	};

	const mecoInboxOnClick = () => {
		if (onboardingProfile?.persistence.mailboxProfile && isDisplayAlert.current) {
			showReplaceMailboxAlert(mecoInboxOnClick);
			return;
		}

		const existingMecoInbox = authUser!.mailbox_profiles.find((profile) => profile.type === NLMailboxProfileType.Internal);
		if (existingMecoInbox) {
			setAlertState({ isShow: true, title: "Already set up", message: "You already have a Meco email address set up: " + existingMecoInbox.email_address, actionButton: "OK", dismissButton: null, actionButtonAction: undefined });
			return;
		}
		navigate("/get-started/meco-mailbox");
	};


	const showReplaceMailboxAlert = (actionButtonAction: () => void) => {
		setAlertState({ isShow: true, title: "Your previous newsletter selections will be gone", message: "Starting a new integration will clear your previous newsletter selections. Would you like to continue?", actionButton: "Continue", dismissButton: "Cancel", actionButtonAction: () => {
			isDisplayAlert.current = false;
			deleteOnboardingMailboxProfile();
			actionButtonAction();
		} });
	};

    const deleteOnboardingMailboxProfile = () => {
		if (!onboardingProfile?.persistence.mailboxProfile || !setOnboardingProfile) {
			return;
		}
		setLoadingState({ isLoading: true, message: "Give us a moment" });
		deleteMailboxProfile(onboardingProfile!.persistence.mailboxProfile!.id).then(() => {
			const tempOnboardingProfile = { ...onboardingProfile };
			tempOnboardingProfile.persistence.feedArray = []
			tempOnboardingProfile.persistence.mailboxProfile = undefined
			tempOnboardingProfile.persistence.oneClickSubscribeArray = []
			tempOnboardingProfile.persistence.prospectArray = []
			tempOnboardingProfile.persistence.prospectLoaded = false
			tempOnboardingProfile.persistence.lastGetUserMailStatesDateString = null
			setOnboardingProfile(tempOnboardingProfile);
			setLoadingState({ isLoading: false });
		}).catch((error) => {
			console.error(error);
		});
	};

	return (
		<div>
			<ParticlesBackground />
            <LoadingView loadingState={loadingState} />
            <Alert alertState={alertState} setAlertState={setAlertState} />
			<div className="flex min-h-[100dvh] flex-col md:justify-center justify-start overflow-hidden py-2 md:py-12">
				<div className="flex flex-col gap-10 w-full items-center justify-center p-3">
					<div className="meco_container max-w-[1080px] py-[40px] md:p-[60px]">
						<div className="flex flex-col md:flex-row gap-5 items-start mt-[50px] md:mt-[0px] w-full">
							<div className="w-full md:w-1/2 flex flex-col gap-3 md:items-start items-center md:text-left text-center">
								<div className={`text-white font-bold font-primary text-4xl leading-10`}>Add a mailbox</div>
								<div className="text-force-primary-light font-medium font-primary text-base  md:text-xl">Create a Meco email address or integrate your existing Gmail or Outlook inbox to start using Meco.</div>
							</div>
							<div className="w-full md:w-1/2 flex flex-col items-center justify-center gap-3">
								<div className="flex flex-row flex-wrap gap-4 justify-center">
                                {addMailboxCards.map((card, index) => {
										return (
											<button key={index} className="flex flex-col hover:opacity-100 opacity-90 duration-300 transition ease-in-out w-full item rounded-[15px] p-3 gap-3 shadow-md" style={{ backgroundColor: card.color }} onClick={card.onClick}>
												<div className="flex flex-row items-center justify-between gap-2 w-full">
													<div className="flex flex-row items-center gap-2">
														<div className="w-[40px] h-[40px] bg-white border border-brand-blue/20 rounded-md flex flex-row justify-center items-center">{card.icon}</div>
														<div className="flex flex-col items-start">
															<div className="font-primary font-medium text-xl text-white">{card.title}</div>
															{card.trialBadge && <div className="font-primary text-xs text-white">🎁 TRY 7 DAYS FOR FREE</div>}
														</div>
													</div>
													<FilledArrow className="fill-white w-[32px] h-[32px]" />
												</div>
												<div className="font-primary font-regular text-base text-white text-left">{card.description}</div>
											</button>
										);
									})}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Integration;